import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Loader2, Plus, Edit, Trash2, Users, Award, FileText, Star, 
  Building2, Calendar, MapPin, DollarSign, Eye, CheckSquare,
  BarChart3, ClipboardList, Settings, MessageSquare, Send, Download, UserPlus, Shield, Brain
} from 'lucide-react';
import { programsAPI, applicationsAPI, evaluatorsAPI, usersAPI, messagesAPI, orgUsersAPI, getImageUrl } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import FormBuilder from '../components/FormBuilder';
import HMWAssistant from '../components/HMWAssistant';
import RichTextEditor from '../components/RichTextEditor';
import DraggablePrizes from '../components/DraggablePrizes';
import { PlanBadge, UsageCounter } from '../components/PlanBadge';
import { PaywallModal } from '../components/PaywallModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activeOrgTab, setActiveOrgTab] = useState('programs');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [messageForm, setMessageForm] = useState({ programId: '', subject: '', body: '', recipientFilter: 'all' });
  const [sentMessages, setSentMessages] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedMsgProgram, setSelectedMsgProgram] = useState('');
  const [exporting, setExporting] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState('applications');

  // Org users management
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgUsersInfo, setOrgUsersInfo] = useState({ org_admin_count: 0, max_admins: 3, plan: 'free' });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  
  // Dialog states
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [applicationDetailOpen, setApplicationDetailOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applicationScores, setApplicationScores] = useState({});
  
  // Assignment state
  const [selectedEvaluator, setSelectedEvaluator] = useState('');
  const [selectedApplications, setSelectedApplications] = useState([]);
  
  // Program form state
  const [programForm, setProgramForm] = useState({
    name: '',
    organization: '',
    description: '',
    challenge: '',
    prize: '',
    prizes: [],
    startDate: '',
    deadline: '',
    status: 'draft',
    category: 'Technology',
    location: '',
    image: '',
    technicalBasesUrl: '',
    technicalBasesFilename: '',
    targetStages: [],
    stages: ['Application', 'Screening', 'Finals'],
    applicationSteps: [],
    eligibilityRequirements: [],
    customSections: [],
    customFormFields: [],
    verticals: [],
    scoringCriteria: [],
    autoMessages: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [showAdvancedSections, setShowAdvancedSections] = useState(false);
  const [showHMWAssistant, setShowHMWAssistant] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const t = {
    es: {
      title: 'Panel de Organización',
      subtitle: 'Gestiona tus desafíos, postulaciones y evaluadores',
      welcome: 'Bienvenido',
      tabs: {
        overview: 'Resumen',
        programs: 'Mis Desafíos',
        applications: 'Postulaciones',
        evaluators: 'Evaluadores'
      },
      stats: {
        totalPrograms: 'Total Desafíos',
        activePrograms: 'Desafíos Activos',
        totalApplications: 'Total Postulaciones',
        pendingReviews: 'Pendientes de Revisión'
      },
      programs: {
        title: 'Mis Desafíos',
        newProgram: 'Nuevo Desafío',
        editProgram: 'Editar Desafío',
        createProgram: 'Crear Desafío',
        noPrograms: 'No has creado ningún desafío',
        createFirst: 'Crea tu primer desafío para empezar',
        name: 'Nombre del Desafío',
        organization: 'Organización',
        description: 'Descripción',
        prize: 'Premio',
        deadline: 'Fecha Límite',
        status: 'Estado',
        category: 'Categoría',
        location: 'Ubicación',
        image: 'Imagen',
        applicants: 'postulantes',
        viewApplications: 'Ver Postulaciones',
        applicationProcess: 'Proceso de Postulación',
        eligibility: 'Elegibilidad',
        customSections: 'Secciones Personalizadas',
        addSection: 'Agregar Sección',
        sectionTitle: 'Título de la Sección',
        addItem: 'Agregar Item',
        itemPlaceholder: 'Escribe el contenido del item...',
        stepTitle: 'Título del Paso',
        stepDescription: 'Descripción del Paso',
        addStep: 'Agregar Paso',
        requirementPlaceholder: 'Escribe un requisito...',
        addRequirement: 'Agregar Requisito',
        applicationForm: 'Formulario de Postulación',
        configureForm: 'Configurar Formulario',
        statuses: {
          open: 'Abierto',
          closed: 'Cerrado',
          draft: 'Borrador'
        }
      },
      applications: {
        title: 'Postulaciones',
        selectProgram: 'Selecciona un desafío',
        noApplications: 'No hay postulaciones para este desafío',
        startup: 'Startup',
        applicant: 'Postulante',
        email: 'Email',
        stage: 'Etapa',
        score: 'Puntaje',
        status: 'Estado',
        actions: 'Acciones',
        viewDetails: 'Ver Detalles',
        applicationDetails: 'Detalles de Postulación',
        founderInfo: 'Información del Fundador',
        startupInfo: 'Información del Startup',
        teamSize: 'Tamaño del Equipo',
        fundingRaised: 'Financiamiento Recaudado',
        website: 'Sitio Web',
        pitchDeck: 'Pitch Deck',
        notes: 'Notas',
        evaluations: 'Evaluaciones',
        noEvaluations: 'Sin evaluaciones aún',
        avgScore: 'Puntaje Promedio'
      },
      evaluators: {
        title: 'Asignar Evaluadores',
        assignEvaluators: 'Asignar Evaluadores',
        selectEvaluator: 'Seleccionar Evaluador',
        selectApplications: 'Seleccionar Postulaciones',
        noEvaluators: 'No hay evaluadores disponibles',
        assigned: 'asignaciones',
        scores: 'evaluaciones',
        assign: 'Asignar',
        selected: 'seleccionadas',
        alreadyAssigned: 'Ya asignada',
        success: 'Evaluadores asignados correctamente',
        filterByProgram: 'Filtrar por Desafío',
        allPrograms: 'Todos mis desafíos'
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        close: 'Cerrar',
        loading: 'Cargando...',
        error: 'Error al procesar',
        success: 'Operación exitosa',
        confirmDelete: '¿Estás seguro de eliminar este elemento?',
        download: 'Descargar',
        noData: 'Sin datos'
      }
    },
    en: {
      title: 'Organization Dashboard',
      subtitle: 'Manage your challenges, applications, and evaluators',
      welcome: 'Welcome',
      tabs: {
        overview: 'Overview',
        programs: 'My Challenges',
        applications: 'Applications',
        evaluators: 'Evaluators'
      },
      stats: {
        totalPrograms: 'Total Challenges',
        activePrograms: 'Active Challenges',
        totalApplications: 'Total Applications',
        pendingReviews: 'Pending Reviews'
      },
      programs: {
        title: 'My Challenges',
        newProgram: 'New Challenge',
        editProgram: 'Edit Challenge',
        createProgram: 'Create Challenge',
        noPrograms: 'You haven\'t created any challenges',
        createFirst: 'Create your first challenge to get started',
        name: 'Challenge Name',
        organization: 'Organization',
        description: 'Description',
        prize: 'Prize',
        deadline: 'Deadline',
        status: 'Status',
        category: 'Category',
        location: 'Location',
        image: 'Image',
        applicants: 'applicants',
        viewApplications: 'View Applications',
        applicationProcess: 'Application Process',
        eligibility: 'Eligibility',
        customSections: 'Custom Sections',
        addSection: 'Add Section',
        sectionTitle: 'Section Title',
        addItem: 'Add Item',
        itemPlaceholder: 'Write item content...',
        stepTitle: 'Step Title',
        stepDescription: 'Step Description',
        addStep: 'Add Step',
        requirementPlaceholder: 'Write a requirement...',
        addRequirement: 'Add Requirement',
        applicationForm: 'Application Form',
        configureForm: 'Configure Form',
        statuses: {
          open: 'Open',
          closed: 'Closed',
          draft: 'Draft'
        }
      },
      applications: {
        title: 'Applications',
        selectProgram: 'Select a challenge',
        noApplications: 'No applications for this challenge',
        startup: 'Startup',
        applicant: 'Applicant',
        email: 'Email',
        stage: 'Stage',
        score: 'Score',
        status: 'Status',
        actions: 'Actions',
        viewDetails: 'View Details',
        applicationDetails: 'Application Details',
        founderInfo: 'Founder Information',
        startupInfo: 'Startup Information',
        teamSize: 'Team Size',
        fundingRaised: 'Funding Raised',
        website: 'Website',
        pitchDeck: 'Pitch Deck',
        notes: 'Notes',
        evaluations: 'Evaluations',
        noEvaluations: 'No evaluations yet',
        avgScore: 'Average Score'
      },
      evaluators: {
        title: 'Assign Evaluators',
        assignEvaluators: 'Assign Evaluators',
        selectEvaluator: 'Select Evaluator',
        selectApplications: 'Select Applications',
        noEvaluators: 'No evaluators available',
        assigned: 'assignments',
        scores: 'scores',
        assign: 'Assign',
        selected: 'selected',
        alreadyAssigned: 'Already assigned',
        success: 'Evaluators assigned successfully',
        filterByProgram: 'Filter by Challenge',
        allPrograms: 'All my challenges'
      },
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        loading: 'Loading...',
        error: 'Error processing',
        success: 'Operation successful',
        confirmDelete: 'Are you sure you want to delete this item?',
        download: 'Download',
        noData: 'No data'
      }
    }
  }[language];

  const categories = ['Technology', 'Healthcare', 'Sustainability', 'Education', 'Finance', 'Social Impact'];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userId = user._id || user.id;
      
      const [programsData, evaluatorsData, assignmentsData, planData] = await Promise.all([
        programsAPI.getByCreator(userId),
        usersAPI.getEvaluators(),
        evaluatorsAPI.getAssignments(),
        programsAPI.getPlanInfo(userId).catch(() => null)
      ]);
      
      setPrograms(programsData);
      setEvaluators(evaluatorsData);
      setAssignments(assignmentsData);
      if (planData) setPlanInfo(planData);
      
      // Get applications for all programs (pass requester_id for masking)
      const allApps = [];
      for (const program of programsData) {
        try {
          const apps = await applicationsAPI.getByProgram(program._id, userId);
          allApps.push(...apps.map(app => ({ ...app, programName: program.name })));
        } catch (e) {
          console.error('Error fetching apps for program:', program._id);
        }
      }
      setApplications(allApps);

      // Fetch all scores for this organization's programs
      const allScores = {};
      for (const program of programsData) {
        try {
          const programScores = await evaluatorsAPI.getScores({ programId: program._id });
          programScores.forEach(s => {
            if (!allScores[s.applicationId]) allScores[s.applicationId] = [];
            allScores[s.applicationId].push(s);
          });
        } catch (e) {}
      }
      setApplicationScores(allScores);

      // Fetch org users
      try {
        const orgData = await orgUsersAPI.list();
        setOrgUsers(orgData.users || []);
        setOrgUsersInfo({ org_admin_count: orgData.org_admin_count, max_admins: orgData.max_admins, plan: orgData.plan });
      } catch (e) {
        console.error('Error fetching org users:', e);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: t.common.error, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Program functions
  const resetProgramForm = () => {
    setProgramForm({
      name: '',
      organization: user?.organization || '',
      description: '',
      challenge: '',
      prize: '',
      prizes: [],
      deadline: '',
      status: 'draft',
      category: 'Technology',
      location: '',
      image: '',
      technicalBasesUrl: '',
      technicalBasesFilename: '',
      stages: ['Application', 'Screening', 'Finals'],
      applicationSteps: [],
      eligibilityRequirements: [],
      customSections: [],
      customFormFields: []
    });
    setImageFile(null);
    setImagePreview('');
    setEditingProgram(null);
    setDocFile(null);
  };

  const openProgramDialog = (program = null) => {
    // Block creating new challenge on free plan if already at limit
    if (!program && planInfo?.is_free && programs.length >= 1) {
      setPaywallTrigger('challenges');
      setPaywallOpen(true);
      return;
    }
    if (program) {
      setEditingProgram(program);
      setProgramForm({
        name: program.name,
        organization: program.organization,
        description: program.description,
        challenge: program.challenge || '',
        prize: program.prize || '',
        prizes: program.prizes || [],
        startDate: program.startDate?.split('T')[0] || '',
        deadline: program.deadline?.split('T')[0] || '',
        status: program.status,
        category: program.category,
        location: program.location,
        image: program.image,
        technicalBasesUrl: program.technicalBasesUrl || '',
        technicalBasesFilename: program.technicalBasesFilename || '',
        targetStages: program.targetStages || [],
        stages: program.stages || ['Application', 'Screening', 'Finals'],
        applicationSteps: program.applicationSteps || [],
        eligibilityRequirements: program.eligibilityRequirements || [],
        customSections: program.customSections || [],
        customFormFields: program.customFormFields || [],
        verticals: program.verticals || [],
        scoringCriteria: program.scoringCriteria || [],
        autoMessages: program.autoMessages || []
      });
      setImagePreview(getImageUrl(program.image));
      setShowAdvancedSections(
        (program.verticals && program.verticals.length > 0) ||
        (program.customSections && program.customSections.length > 0)
      );
    } else {
      resetProgramForm();
      setShowAdvancedSections(false);
    }
    setDocFile(null);
    setProgramDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return programForm.image;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await fetch(`${API_URL}/api/uploads/program-image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      return data.primary_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProgram = async () => {
    try {
      setSaving(true);
      
      let imageUrl = programForm.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      // Upload document if selected
      let docUrl = programForm.technicalBasesUrl;
      let docFilename = programForm.technicalBasesFilename;
      if (docFile) {
        setUploadingDoc(true);
        const docFormData = new FormData();
        docFormData.append('file', docFile);
        const docResponse = await fetch(`${API_URL}/api/uploads/document`, {
          method: 'POST',
          body: docFormData
        });
        if (docResponse.ok) {
          const docData = await docResponse.json();
          docUrl = docData.url;
          docFilename = docData.filename;
        }
        setUploadingDoc(false);
      }
      
      const programData = {
        ...programForm,
        image: imageUrl,
        technicalBasesUrl: docUrl,
        technicalBasesFilename: docFilename,
        createdBy: user._id || user.id
      };
      
      if (editingProgram) {
        await programsAPI.update(editingProgram._id, programData);
        toast({ title: language === 'es' ? 'Desafío actualizado' : 'Challenge updated' });
      } else {
        await programsAPI.create(programData);
        toast({ title: language === 'es' ? 'Desafío creado' : 'Challenge created' });
      }
      
      setProgramDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving program:', error);
      toast({ title: t.common.error, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    
    try {
      await programsAPI.delete(programId);
      toast({ title: language === 'es' ? 'Desafío eliminado' : 'Challenge deleted' });
      fetchData();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast({ title: t.common.error, variant: 'destructive' });
    }
  };

  // Message functions
  const handleSendMessage = async () => {
    if (!messageForm.programId || !messageForm.subject || !messageForm.body) {
      toast({ title: language === 'es' ? 'Completa todos los campos' : 'Fill all fields', variant: 'destructive' });
      return;
    }
    setSendingMessage(true);
    try {
      const result = await messagesAPI.send(messageForm);
      toast({ title: language === 'es' ? 'Mensaje enviado' : 'Message sent', description: language === 'es' ? `Enviado a ${result.recipientCount} postulantes` : `Sent to ${result.recipientCount} applicants` });
      setMessageForm({ programId: messageForm.programId, subject: '', body: '', recipientFilter: 'all' });
      if (messageForm.programId) {
        const msgs = await messagesAPI.getByProgram(messageForm.programId);
        setSentMessages(msgs);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error';
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    }
    setSendingMessage(false);
  };

  const loadProgramMessages = async (programId) => {
    setSelectedMsgProgram(programId);
    setMessageForm(prev => ({ ...prev, programId }));
    try {
      const msgs = await messagesAPI.getByProgram(programId);
      setSentMessages(msgs);
    } catch (e) {
      setSentMessages([]);
    }
  };


  // Application functions
  const openApplicationDetail = async (application) => {
    // Get scores for this application
    try {
      const scores = await evaluatorsAPI.getScores({ applicationId: application._id });
      setSelectedApplication({ ...application, scores });
    } catch (e) {
      setSelectedApplication({ ...application, scores: [] });
    }
    setApplicationDetailOpen(true);
  };

  // Evaluator assignment functions
  const openAssignDialog = () => {
    setSelectedEvaluator('');
    setSelectedApplications([]);
    setAssignDialogOpen(true);
  };

  const isAlreadyAssigned = (applicationId) => {
    return assignments.some(
      a => a.evaluatorId === selectedEvaluator && a.applicationId === applicationId
    );
  };

  const toggleApplicationSelection = (applicationId) => {
    if (isAlreadyAssigned(applicationId)) return;
    
    setSelectedApplications(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  const handleAssignEvaluators = async () => {
    if (!selectedEvaluator || selectedApplications.length === 0) return;
    
    try {
      setSaving(true);
      await evaluatorsAPI.createBulkAssignments({
        evaluatorId: selectedEvaluator,
        applicationIds: selectedApplications,
        assignedBy: user._id || user.id
      });
      
      toast({ title: t.evaluators.success });
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error assigning evaluators:', error);
      toast({ title: t.common.error, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Stats calculations
  const totalPrograms = programs.length;
  const activePrograms = programs.filter(p => p.status === 'open').length;
  const totalApplications = applications.length;
  const pendingReviews = applications.filter(a => !a.score || a.score === 0).length;

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {t.programs.statuses[status] || status}
      </Badge>
    );
  };

  const getAverageScore = (appId) => {
    const appScores = applicationScores[appId];
    if (!appScores || appScores.length === 0) return null;
    const avg = appScores.reduce((sum, s) => sum + (s.weightedScore || s.totalScore || 0), 0) / appScores.length;
    return avg.toFixed(1);
  };

  const handleExportAll = async () => {
    if (programs.length === 0) return;
    try {
      setExporting(true);
      // Export for the first program with applications, or the first program
      const targetProgram = programs.find(p => applications.some(a => a.programId === p._id)) || programs[0];
      const blob = await applicationsAPI.exportExcel(targetProgram._id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${targetProgram.name?.replace(/\s+/g, '_') || 'postulaciones'}_postulaciones.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: language === 'es' ? 'Archivo descargado' : 'File downloaded' });
    } catch (e) {
      console.error('Export error:', e);
      toast({ title: language === 'es' ? 'Error al exportar' : 'Export error', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportProgram = async (programId, programName) => {
    // Block export for free plan
    if (planInfo?.is_free) {
      setPaywallTrigger('export');
      setPaywallOpen(true);
      return;
    }
    try {
      setExporting(true);
      const blob = await applicationsAPI.exportExcel(programId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${programName?.replace(/\s+/g, '_') || 'postulaciones'}_postulaciones.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: language === 'es' ? 'Archivo descargado' : 'File downloaded' });
    } catch (e) {
      console.error('Export error:', e);
      toast({ title: language === 'es' ? 'Error al exportar' : 'Export error', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  // Org user management functions
  const openUserDialog = (usr = null) => {
    setEditingUser(usr || { name: '', email: '', password: '', role: 'evaluator', phone: '', jobTitle: '' });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser.name || !editingUser.email) {
      toast({ title: language === 'es' ? 'Nombre y email son requeridos' : 'Name and email are required', variant: 'destructive' });
      return;
    }
    if (!editingUser._id && (!editingUser.password || editingUser.password.length < 6)) {
      toast({ title: language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    try {
      setSavingUser(true);
      if (editingUser._id) {
        const payload = { name: editingUser.name, email: editingUser.email, phone: editingUser.phone, jobTitle: editingUser.jobTitle };
        if (editingUser.password) payload.password = editingUser.password;
        await orgUsersAPI.update(editingUser._id, payload);
      } else {
        await orgUsersAPI.create(editingUser);
      }
      setUserDialogOpen(false);
      const orgData = await orgUsersAPI.list();
      setOrgUsers(orgData.users || []);
      setOrgUsersInfo({ org_admin_count: orgData.org_admin_count, max_admins: orgData.max_admins, plan: orgData.plan });
      toast({ title: language === 'es' ? 'Usuario guardado' : 'User saved' });
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.startsWith('PLAN_LIMIT')) {
        setPaywallTrigger('applications');
        setPaywallOpen(true);
      } else {
        toast({ title: 'Error', description: detail || e.message, variant: 'destructive' });
      }
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este usuario?' : 'Delete this user?')) return;
    try {
      await orgUsersAPI.remove(userId);
      const orgData = await orgUsersAPI.list();
      setOrgUsers(orgData.users || []);
      setOrgUsersInfo({ org_admin_count: orgData.org_admin_count, max_admins: orgData.max_admins, plan: orgData.plan });
      toast({ title: language === 'es' ? 'Usuario eliminado' : 'User deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef0f4] flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef0f4] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t.title}</h1>
                <PlanBadge plan={planInfo?.plan} />
              </div>
              <p className="text-gray-500 mt-1">{t.welcome}, {user?.name}</p>
            </div>
          </div>
          {/* Usage counters for free plan */}
          {planInfo?.is_free && (
            <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <UsageCounter
                  used={planInfo.usage.challenges}
                  limit={planInfo.limits.max_challenges}
                  label={language === 'es' ? 'Desafios usados' : 'Challenges used'}
                  onUpgrade={() => { setPaywallTrigger('challenges'); setPaywallOpen(true); }}
                />
              </div>
              <div className="flex-1">
                <UsageCounter
                  used={Math.min(planInfo.usage.total_applications, planInfo.limits.max_visible_applications)}
                  limit={planInfo.limits.max_visible_applications}
                  label={language === 'es' ? 'Postulaciones desbloqueadas' : 'Applications unlocked'}
                  onUpgrade={() => { setPaywallTrigger('applications'); setPaywallOpen(true); }}
                />
              </div>
            </div>
          )}
        </div>

        <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} trigger={paywallTrigger} />

        {/* User Create/Edit Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingUser?._id
                  ? (language === 'es' ? 'Editar Usuario' : 'Edit User')
                  : (language === 'es' ? 'Nuevo Usuario' : 'New User')}
              </DialogTitle>
              <DialogDescription>
                {language === 'es'
                  ? 'Crea administradores para tu organizacion o evaluadores humanos.'
                  : 'Create admins for your organization or human evaluators.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!editingUser?._id && (
                <div>
                  <Label>{language === 'es' ? 'Tipo de usuario' : 'User type'}</Label>
                  <Select value={editingUser?.role || 'evaluator'} onValueChange={(v) => setEditingUser(prev => ({ ...prev, role: v }))}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organization">
                        {language === 'es' ? 'Administrador de organizacion' : 'Organization Admin'}
                        <span className="text-xs text-gray-400 ml-1">({orgUsersInfo.org_admin_count}/{orgUsersInfo.max_admins})</span>
                      </SelectItem>
                      <SelectItem value="evaluator">{language === 'es' ? 'Evaluador humano' : 'Human Evaluator'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{language === 'es' ? 'Nombre completo' : 'Full name'}</Label>
                <Input value={editingUser?.name || ''} onChange={e => setEditingUser(prev => ({ ...prev, name: e.target.value }))} data-testid="user-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editingUser?.email || ''} onChange={e => setEditingUser(prev => ({ ...prev, email: e.target.value }))} data-testid="user-email-input" />
              </div>
              <div>
                <Label>{editingUser?._id ? (language === 'es' ? 'Nueva contraseña (dejar vacio para no cambiar)' : 'New password (leave empty to keep)') : (language === 'es' ? 'Contraseña' : 'Password')}</Label>
                <Input type="password" value={editingUser?.password || ''} onChange={e => setEditingUser(prev => ({ ...prev, password: e.target.value }))} placeholder={editingUser?._id ? '••••••' : ''} data-testid="user-password-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Telefono' : 'Phone'}</Label>
                  <Input value={editingUser?.phone || ''} onChange={e => setEditingUser(prev => ({ ...prev, phone: e.target.value }))} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Cargo' : 'Job title'}</Label>
                  <Input value={editingUser?.jobTitle || ''} onChange={e => setEditingUser(prev => ({ ...prev, jobTitle: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserDialogOpen(false)}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
              <Button onClick={handleSaveUser} disabled={savingUser} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-user-btn">
                {savingUser && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {language === 'es' ? 'Guardar' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - Left */}
          <div className="flex-1 min-w-0">
            {/* Tab Navigation - Big Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-6">
              <button
                onClick={() => setActiveOrgTab('programs')}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 font-semibold transition-all ${
                  activeOrgTab === 'programs'
                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-200'
                    : 'bg-white text-gray-700 border-gray-100 hover:border-purple-300 hover:shadow-sm'
                }`}
              >
                <Building2 className="w-6 h-6" />
                <span className="text-sm">{t.tabs.programs}</span>
              </button>
              <button
                onClick={() => setActiveOrgTab('applications')}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 font-semibold transition-all ${
                  activeOrgTab === 'applications'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200'
                    : 'bg-white text-gray-700 border-gray-100 hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                <FileText className="w-6 h-6" />
                <span className="text-sm">{t.tabs.applications}</span>
              </button>
              <button
                onClick={() => setActiveOrgTab('evaluators')}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 font-semibold transition-all ${
                  activeOrgTab === 'evaluators'
                    ? 'bg-green-600 text-white border-green-600 shadow-lg shadow-green-200'
                    : 'bg-white text-gray-700 border-gray-100 hover:border-green-300 hover:shadow-sm'
                }`}
              >
                <Users className="w-6 h-6" />
                <span className="text-sm">{t.tabs.evaluators}</span>
              </button>
              <button
                onClick={() => setActiveOrgTab('messages')}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 font-semibold transition-all ${
                  activeOrgTab === 'messages'
                    ? 'bg-orange-600 text-white border-orange-600 shadow-lg shadow-orange-200'
                    : 'bg-white text-gray-700 border-gray-100 hover:border-orange-300 hover:shadow-sm'
                }`}
              >
                <MessageSquare className="w-6 h-6" />
                <span className="text-sm">{language === 'es' ? 'Mensajes' : 'Messages'}</span>
              </button>
              <button
                onClick={() => setActiveOrgTab('users')}
                data-testid="org-users-tab"
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 font-semibold transition-all ${
                  activeOrgTab === 'users'
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200'
                    : 'bg-white text-gray-700 border-gray-100 hover:border-indigo-300 hover:shadow-sm'
                }`}
              >
                <Shield className="w-6 h-6" />
                <span className="text-sm">{language === 'es' ? 'Usuarios' : 'Users'}</span>
              </button>
            </div>

            {/* Tab Content */}
            {activeOrgTab === 'programs' && (
            <Card className="glass-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t.programs.title}</CardTitle>
                  <CardDescription>{t.subtitle}</CardDescription>
                </div>
                <Button onClick={() => openProgramDialog()} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  {t.programs.newProgram}
                </Button>
              </CardHeader>
              <CardContent>
                {programs.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900">{t.programs.noPrograms}</h3>
                    <p className="text-gray-600 mt-2">{t.programs.createFirst}</p>
                    <Button onClick={() => openProgramDialog()} className="mt-4 bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t.programs.newProgram}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {programs.map((program) => (
                      <Card key={program._id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <div 
                          className="h-40 overflow-hidden cursor-pointer"
                          onClick={() => openProgramDialog(program)}
                        >
                          <img
                            src={getImageUrl(program.image) || 'https://via.placeholder.com/400x200'}
                            alt={program.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-semibold text-lg">{program.name}</h3>
                            {getStatusBadge(program.status)}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{program.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {program.applicants} {t.programs.applicants}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(program.deadline).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex gap-2 mb-2">
                            <Button 
                              size="sm"
                              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                              data-testid={`challenge-dashboard-btn-${program._id}`}
                              asChild
                            >
                              <Link to={`/organization/challenge/${program._id}`}>
                                <BarChart3 className="w-4 h-4 mr-1" />
                                Dashboard
                              </Link>
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => openProgramDialog(program)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {t.common.edit}
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              asChild
                            >
                              <Link to={`/programs/${program._id}`} target="_blank">
                                <Eye className="w-4 h-4 mr-1" />
                                {language === 'es' ? 'Ver Desafío' : 'View Challenge'}
                              </Link>
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleDeleteProgram(program._id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Applications Tab */}
            {activeOrgTab === 'applications' && (
            <Card className="glass-card">
              <CardHeader className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <CardTitle>{t.applications.title}</CardTitle>
                  <CardDescription>
                    {language === 'es' 
                      ? 'Ve todas las postulaciones a tus desafíos'
                      : 'View all applications to your programs'}
                  </CardDescription>
                </div>
                {applications.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select onValueChange={(val) => handleExportProgram(val, programs.find(p => p._id === val)?.name)}>
                      <SelectTrigger className="w-full sm:w-[200px] border-emerald-300" data-testid="export-program-select">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Download className="w-4 h-4" />
                          {language === 'es' ? 'Exportar por desafío' : 'Export by challenge'}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {programs.map(p => (
                          <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                {applications.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>{t.applications.noApplications}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.applications.startup}</TableHead>
                        <TableHead>{t.applications.applicant}</TableHead>
                        <TableHead>{t.programs.title.replace('Mis ', '')}</TableHead>
                        <TableHead>{t.applications.stage}</TableHead>
                        <TableHead>{t.applications.score}</TableHead>
                        <TableHead className="text-right">{t.applications.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.map((app) => {
                        const avgScore = getAverageScore(app._id);
                        return (
                          <TableRow key={app._id}>
                            <TableCell className="font-medium">{app.startupName}</TableCell>
                            <TableCell>
                              <div>
                                <p>{app.founderName}</p>
                                <p className="text-sm text-gray-500">{app.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{app.programName}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{app.stage}</Badge>
                            </TableCell>
                            <TableCell>
                              {avgScore ? (
                                <span className="flex items-center gap-1 text-yellow-600 font-semibold">
                                  <Star className="w-4 h-4" />
                                  {avgScore}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openApplicationDetail(app)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                {t.applications.viewDetails}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Messages Tab */}
            {activeOrgTab === 'messages' && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  {language === 'es' ? 'Mensajes a Postulantes' : 'Messages to Applicants'}
                </CardTitle>
                <CardDescription>
                  {language === 'es'
                    ? 'Envía mensajes internos y por email a los postulantes de tus desafíos'
                    : 'Send internal and email messages to your challenge applicants'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Program Selector */}
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Selecciona un Desafío' : 'Select a Challenge'}</Label>
                  <Select value={selectedMsgProgram} onValueChange={loadProgramMessages}>
                    <SelectTrigger data-testid="msg-program-select">
                      <SelectValue placeholder={language === 'es' ? 'Elige un desafío...' : 'Choose a challenge...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedMsgProgram && (
                  <>
                    {/* Compose Message */}
                    <div className="border-2 border-orange-200 rounded-lg p-5 bg-orange-50 space-y-4">
                      <h4 className="font-semibold text-orange-900 flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {language === 'es' ? 'Nuevo Mensaje' : 'New Message'}
                      </h4>

                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Destinatarios' : 'Recipients'}</Label>
                        <Select
                          value={messageForm.recipientFilter}
                          onValueChange={(val) => setMessageForm({ ...messageForm, recipientFilter: val })}
                        >
                          <SelectTrigger data-testid="msg-recipient-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{language === 'es' ? 'Todos los postulantes' : 'All applicants'}</SelectItem>
                            <SelectItem value="submitted">{language === 'es' ? 'Enviadas' : 'Submitted'}</SelectItem>
                            <SelectItem value="under_review">{language === 'es' ? 'En Revisión' : 'Under Review'}</SelectItem>
                            <SelectItem value="finalist">{language === 'es' ? 'Finalistas' : 'Finalists'}</SelectItem>
                            <SelectItem value="accepted">{language === 'es' ? 'Aceptados' : 'Accepted'}</SelectItem>
                            <SelectItem value="rejected">{language === 'es' ? 'Rechazados' : 'Rejected'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Asunto' : 'Subject'}</Label>
                        <Input
                          data-testid="msg-subject"
                          value={messageForm.subject}
                          onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                          placeholder={language === 'es' ? 'Asunto del mensaje...' : 'Message subject...'}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{language === 'es' ? 'Mensaje' : 'Message'}</Label>
                        <Textarea
                          data-testid="msg-body"
                          value={messageForm.body}
                          onChange={(e) => setMessageForm({ ...messageForm, body: e.target.value })}
                          rows={5}
                          placeholder={language === 'es' ? 'Escribe tu mensaje aquí...' : 'Write your message here...'}
                        />
                      </div>

                      <Button
                        data-testid="msg-send-btn"
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !messageForm.subject || !messageForm.body}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        {language === 'es' ? 'Enviar Mensaje' : 'Send Message'}
                      </Button>
                    </div>

                    {/* Sent Messages History */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {language === 'es' ? 'Mensajes Enviados' : 'Sent Messages'}
                        <Badge variant="secondary">{sentMessages.length}</Badge>
                      </h4>

                      {sentMessages.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                          <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p>{language === 'es' ? 'No hay mensajes enviados para este desafío' : 'No messages sent for this challenge'}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {sentMessages.map((msg, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
                              <div className="flex items-start justify-between mb-2">
                                <h5 className="font-semibold text-gray-900">{msg.subject}</h5>
                                <span className="text-xs text-gray-500 shrink-0 ml-3">
                                  {new Date(msg.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{msg.body}</p>
                              <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100">
                                <Badge variant="outline" className="text-xs">
                                  <Users className="w-3 h-3 mr-1" />
                                  {msg.recipientCount} {language === 'es' ? 'destinatarios' : 'recipients'}
                                </Badge>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {msg.recipientFilter === 'all'
                                    ? (language === 'es' ? 'Todos' : 'All')
                                    : msg.recipientFilter}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!selectedMsgProgram && (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>{language === 'es' ? 'Selecciona un desafío para enviar mensajes' : 'Select a challenge to send messages'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Evaluators Tab */}
            {activeOrgTab === 'evaluators' && (
            <Card className="glass-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t.evaluators.title}</CardTitle>
                  <CardDescription>
                    {language === 'es' 
                      ? 'Asigna evaluadores a las postulaciones de tus desafíos'
                      : 'Assign evaluators to applications for your programs'}
                  </CardDescription>
                </div>
                <Button 
                  onClick={openAssignDialog} 
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={evaluators.length === 0 || applications.length === 0}
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  {t.evaluators.assignEvaluators}
                </Button>
              </CardHeader>
              <CardContent>
                {evaluators.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>{t.evaluators.noEvaluators}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {evaluators.map((evaluator) => {
                      const evalAssignments = assignments.filter(a => 
                        a.evaluatorId === evaluator._id && 
                        programs.some(p => p._id === a.programId)
                      );
                      return (
                        <Card key={evaluator._id} className="border-2">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold">{evaluator.name}</h4>
                                <p className="text-sm text-gray-500">{evaluator.email}</p>
                              </div>
                            </div>
                            <div className="flex gap-4 text-sm">
                              <span className="text-yellow-600">
                                {evalAssignments.length} {t.evaluators.assigned}
                              </span>
                              <span className="text-green-600">
                                {evalAssignments.filter(a => a.status === 'completed').length} {t.evaluators.scores}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Users Tab */}
            {activeOrgTab === 'users' && (
            <Card className="glass-card" data-testid="org-users-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{language === 'es' ? 'Gestion de Usuarios' : 'User Management'}</CardTitle>
                  <CardDescription>
                    {language === 'es'
                      ? `Administradores: ${orgUsersInfo.org_admin_count}/${orgUsersInfo.max_admins} (Plan ${orgUsersInfo.plan})`
                      : `Admins: ${orgUsersInfo.org_admin_count}/${orgUsersInfo.max_admins} (${orgUsersInfo.plan} plan)`}
                  </CardDescription>
                </div>
                <Button onClick={() => openUserDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Nuevo Usuario' : 'New User'}
                </Button>
              </CardHeader>
              <CardContent>
                {orgUsers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>{language === 'es' ? 'No hay usuarios asociados' : 'No associated users'}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Table header */}
                    <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                      <div className="col-span-3">{language === 'es' ? 'Nombre' : 'Name'}</div>
                      <div className="col-span-3">Email</div>
                      <div className="col-span-2">{language === 'es' ? 'Rol' : 'Role'}</div>
                      <div className="col-span-2">{language === 'es' ? 'Cargo' : 'Title'}</div>
                      <div className="col-span-2 text-right">{language === 'es' ? 'Acciones' : 'Actions'}</div>
                    </div>
                    {orgUsers.filter(u => u._id !== (user?._id || user?.id)).map((u) => (
                      <div key={u._id} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center px-4 py-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200" data-testid={`org-user-${u._id}`}>
                        <div className="sm:col-span-3 flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${u.role === 'organization' ? 'bg-indigo-500' : 'bg-green-500'}`}>
                            {u.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{u.name}</span>
                        </div>
                        <div className="sm:col-span-3 text-sm text-gray-600 truncate">{u.email}</div>
                        <div className="sm:col-span-2">
                          <Badge variant="secondary" className={`text-xs ${u.role === 'organization' ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'}`}>
                            {u.role === 'organization' ? (language === 'es' ? 'Admin' : 'Admin') : (language === 'es' ? 'Evaluador' : 'Evaluator')}
                          </Badge>
                        </div>
                        <div className="sm:col-span-2 text-sm text-gray-500">{u.jobTitle || '-'}</div>
                        <div className="sm:col-span-2 flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openUserDialog(u)} data-testid={`edit-user-${u._id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50" onClick={() => handleDeleteUser(u._id)} data-testid={`delete-user-${u._id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </div>

          {/* Stats Sidebar - Right */}
          <div className="lg:w-72 shrink-0">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
            <div className="glass-sidebar rounded-xl border-2 border-purple-200/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">{t.stats.totalPrograms}</p>
                  <p className="text-3xl font-bold text-purple-900 mt-1">{totalPrograms}</p>
                </div>
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
            </div>
            <div className="glass-sidebar rounded-xl border-2 border-green-200/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-700 uppercase tracking-wide">{t.stats.activePrograms}</p>
                  <p className="text-3xl font-bold text-green-900 mt-1">{activePrograms}</p>
                </div>
                <Award className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="glass-sidebar rounded-xl border-2 border-blue-200/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">{t.stats.totalApplications}</p>
                  <p className="text-3xl font-bold text-blue-900 mt-1">{totalApplications}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="glass-sidebar rounded-xl border-2 border-yellow-200/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-yellow-700 uppercase tracking-wide">{t.stats.pendingReviews}</p>
                  <p className="text-3xl font-bold text-yellow-900 mt-1">{pendingReviews}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Program Dialog */}
        <Dialog open={programDialogOpen} onOpenChange={setProgramDialogOpen}>
          <DialogContent className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>
                {editingProgram ? t.programs.editProgram : t.programs.createProgram}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.programs.name} *</Label>
                  <Input
                    value={programForm.name}
                    onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.programs.organization}</Label>
                  <Input
                    value={programForm.organization}
                    onChange={(e) => setProgramForm({ ...programForm, organization: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t.programs.description}</Label>
                <p className="text-xs text-gray-500">
                  {language === 'es'
                    ? 'Describe de que se trata este desafio: el contexto, la problematica que motiva el desafio, los resultados esperados y cualquier informacion relevante para los postulantes.'
                    : 'Describe what this challenge is about: the context, the problem motivating the challenge, expected outcomes and any relevant information for applicants.'}
                </p>
                <Textarea
                  value={programForm.description}
                  onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                  rows={4}
                  placeholder={language === 'es'
                    ? 'Ej: Este desafio busca soluciones innovadoras para mejorar el acceso a educacion en zonas rurales. El contexto es...'
                    : 'E.g.: This challenge seeks innovative solutions to improve access to education in rural areas. The context is...'}
                />
              </div>

              <div className="space-y-3">
                <div>
                  <Label>{language === 'es' ? 'El Desafio' : 'The Challenge'}</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'es'
                      ? 'Define la pregunta clave de tu desafio. Una buena pregunta estilo "Como podriamos..." (How Might We) inspira mejores postulaciones, enfoca las soluciones y atrae al talento correcto.'
                      : 'Define the key question of your challenge. A good "How Might We..." question inspires better applications, focuses solutions and attracts the right talent.'}
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-purple-100 rounded-lg shrink-0">
                      <Brain className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-purple-900">
                        {language === 'es' ? 'Asistente IA para formular tu desafio' : 'AI Assistant to formulate your challenge'}
                      </p>
                      <p className="text-xs text-purple-700 mt-0.5">
                        {language === 'es'
                          ? 'Responde 4 preguntas y la IA generara una frase de desafio optimizada para recibir las mejores postulaciones.'
                          : 'Answer 4 questions and AI will generate an optimized challenge statement to receive the best applications.'}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => setShowHMWAssistant(true)}
                        data-testid="open-hmw-assistant-btn"
                      >
                        <Brain className="w-4 h-4 mr-2" />
                        {language === 'es' ? 'Usar Asistente IA' : 'Use AI Assistant'}
                      </Button>
                    </div>
                  </div>
                </div>

                <RichTextEditor
                  value={programForm.challenge || ''}
                  onChange={(val) => setProgramForm({ ...programForm, challenge: val })}
                  placeholder={language === 'es' ? 'Ej: Como podriamos reducir la brecha digital en comunidades rurales...' : 'e.g. How might we reduce the digital gap in rural communities...'}
                />
              </div>

              {/* Flexible Prizes Section with Drag & Drop */}
              <DraggablePrizes
                prizes={programForm.prizes || []}
                onChange={(newPrizes) => setProgramForm({ ...programForm, prizes: newPrizes })}
                language={language}
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Fecha de Inicio' : 'Start Date'}</Label>
                  <Input
                    type="date"
                    value={programForm.startDate}
                    onChange={(e) => setProgramForm({ ...programForm, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.programs.deadline}</Label>
                  <Input
                    type="date"
                    value={programForm.deadline}
                    onChange={(e) => setProgramForm({ ...programForm, deadline: e.target.value })}
                  />
                </div>
              </div>

              {/* Target Startup Stages */}
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Etapas de Startups' : 'Target Startup Stages'}</Label>
                <div className="flex flex-wrap gap-2">
                  {['Idea', 'Pre-Seed', 'Seed', 'MVP', 'Early Stage', 'Growth', 'Scale'].map((stage) => {
                    const selected = programForm.targetStages?.includes(stage);
                    return (
                      <button
                        key={stage}
                        type="button"
                        onClick={() => {
                          const current = programForm.targetStages || [];
                          setProgramForm({
                            ...programForm,
                            targetStages: selected
                              ? current.filter(s => s !== stage)
                              : [...current, stage]
                          });
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          selected
                            ? 'bg-purple-100 border-purple-400 text-purple-800'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {stage}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Bases Tecnicas (PDF/Word)' : 'Technical Bases (PDF/Word)'}</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setDocFile(file);
                    }}
                  />
                  {(programForm.technicalBasesFilename || docFile) && (
                    <p className="text-xs text-green-600">
                      {docFile ? docFile.name : programForm.technicalBasesFilename}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t.programs.status}</Label>
                  <Select
                    value={programForm.status}
                    onValueChange={(value) => setProgramForm({ ...programForm, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t.programs.statuses.draft}</SelectItem>
                      <SelectItem value="open">{t.programs.statuses.open}</SelectItem>
                      <SelectItem value="closed">{t.programs.statuses.closed}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.programs.category}</Label>
                  <Select
                    value={programForm.category}
                    onValueChange={(value) => setProgramForm({ ...programForm, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.programs.location}</Label>
                  <Input
                    value={programForm.location}
                    onChange={(e) => setProgramForm({ ...programForm, location: e.target.value })}
                  />
                </div>
              </div>

              {/* Image Upload - moved above optional sections */}
              <div className="space-y-2">
                <Label>{t.programs.image}</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="h-32 object-cover rounded mt-2" />
                )}
              </div>

              {/* Scoring Criteria Section */}
              <div className="space-y-4 pt-4 border-t border-gray-300">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <Label className="text-lg font-semibold">
                      {language === 'es' ? 'Criterios de Evaluacion' : 'Evaluation Criteria'}
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'es'
                        ? 'Define los criterios con los que los evaluadores calificaran las postulaciones (escala 1-10).'
                        : 'Define the criteria evaluators will use to score applications (1-10 scale).'}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="load-criteria-template-btn"
                      onClick={() => {
                        const template = language === 'es'
                          ? [
                              { name: 'Innovacion', description: 'Nivel de innovacion, originalidad y diferenciacion de la solucion', maxScore: 10, weight: 1.0 },
                              { name: 'Viabilidad', description: 'Viabilidad tecnica, comercial y del modelo de negocio', maxScore: 10, weight: 1.0 },
                              { name: 'Equipo', description: 'Experiencia, complementariedad y capacidad de ejecucion del equipo', maxScore: 10, weight: 1.0 },
                              { name: 'Impacto', description: 'Impacto potencial social, ambiental o economico de la solucion', maxScore: 10, weight: 1.0 },
                              { name: 'Escalabilidad', description: 'Capacidad de escalar la solucion a nuevos mercados o segmentos', maxScore: 10, weight: 1.0 },
                            ]
                          : [
                              { name: 'Innovation', description: 'Level of innovation, originality and differentiation', maxScore: 10, weight: 1.0 },
                              { name: 'Feasibility', description: 'Technical, commercial and business model feasibility', maxScore: 10, weight: 1.0 },
                              { name: 'Team', description: 'Experience, complementarity and execution capacity', maxScore: 10, weight: 1.0 },
                              { name: 'Impact', description: 'Potential social, environmental or economic impact', maxScore: 10, weight: 1.0 },
                              { name: 'Scalability', description: 'Ability to scale to new markets or segments', maxScore: 10, weight: 1.0 },
                            ];
                        setProgramForm({ ...programForm, scoringCriteria: template });
                      }}
                      className="border-amber-400 text-amber-800 hover:bg-amber-50 font-semibold"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      {language === 'es' ? 'Cargar Plantilla' : 'Load Template'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setProgramForm({
                        ...programForm,
                        scoringCriteria: [...(programForm.scoringCriteria || []), { name: '', description: '', maxScore: 10, weight: 1.0 }]
                      })}
                      className="border-2"
                    >
                      <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Agregar Criterio' : 'Add Criterion'}
                    </Button>
                  </div>
                </div>

                {(programForm.scoringCriteria || []).length === 0 && (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500">
                      {language === 'es'
                        ? 'Sin criterios definidos. Se usaran criterios por defecto (Innovacion, Viabilidad, Equipo, Impacto).'
                        : 'No criteria defined. Default criteria will be used (Innovation, Feasibility, Team, Impact).'}
                    </p>
                  </div>
                )}

                {(programForm.scoringCriteria || []).map((criterion, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">{language === 'es' ? 'Nombre del criterio' : 'Criterion name'}</Label>
                          <Input
                            placeholder={language === 'es' ? 'Ej: Innovacion' : 'E.g.: Innovation'}
                            value={criterion.name}
                            onChange={(e) => {
                              const newCriteria = [...programForm.scoringCriteria];
                              newCriteria[index].name = e.target.value;
                              setProgramForm({ ...programForm, scoringCriteria: newCriteria });
                            }}
                            className="font-semibold"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">{language === 'es' ? 'Descripcion' : 'Description'}</Label>
                          <Input
                            placeholder={language === 'es' ? 'Que debe evaluar el evaluador' : 'What the evaluator should assess'}
                            value={criterion.description}
                            onChange={(e) => {
                              const newCriteria = [...programForm.scoringCriteria];
                              newCriteria[index].description = e.target.value;
                              setProgramForm({ ...programForm, scoringCriteria: newCriteria });
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const newCriteria = programForm.scoringCriteria.filter((_, i) => i !== index);
                          setProgramForm({ ...programForm, scoringCriteria: newCriteria });
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Advanced Sections Toggle */}
              <div className="border-2 border-gray-200 rounded-lg p-4" data-testid="advanced-sections-toggle">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold text-gray-800">
                      {language === 'es' ? 'Robustecer la publicacion' : 'Enhance the publication'}
                    </Label>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {language === 'es'
                        ? 'Agrega verticales, secciones descriptivas y mas detalles al desafio.'
                        : 'Add verticals, descriptive sections and more details to the challenge.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedSections(!showAdvancedSections)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showAdvancedSections ? 'bg-purple-600' : 'bg-gray-300'}`}
                    data-testid="advanced-toggle-switch"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showAdvancedSections ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {showAdvancedSections && (
                <div className="space-y-6 border-2 border-purple-100 rounded-lg p-5 bg-purple-50/30">
                  {/* Verticals / Sub-categories */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Verticales' : 'Verticals'}</Label>
                    <p className="text-xs text-gray-500">
                      {language === 'es' 
                        ? 'Agrega sub-categorias especificas. Ej: Fintech, Cripto, Insurtech, etc.'
                        : 'Add specific sub-categories. E.g.: Fintech, Crypto, Insurtech, etc.'}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(programForm.verticals || []).map((v, i) => (
                        <Badge key={i} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                          {v}
                          <button
                            type="button"
                            className="ml-1 hover:text-red-600"
                            onClick={() => setProgramForm({
                              ...programForm,
                              verticals: programForm.verticals.filter((_, idx) => idx !== i)
                            })}
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="org-vertical-input"
                        placeholder={language === 'es' ? 'Ej: Fintech' : 'E.g.: Fintech'}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.target.value.trim();
                            if (val && !(programForm.verticals || []).includes(val)) {
                              setProgramForm({
                                ...programForm,
                                verticals: [...(programForm.verticals || []), val]
                              });
                              e.target.value = '';
                            }
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const input = document.getElementById('org-vertical-input');
                          const val = input?.value?.trim();
                          if (val && !(programForm.verticals || []).includes(val)) {
                            setProgramForm({
                              ...programForm,
                              verticals: [...(programForm.verticals || []), val]
                            });
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Custom Sections */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 space-y-4 bg-white">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div>
                        <Label className="text-lg font-semibold">{language === 'es' ? 'Secciones del Desafio' : 'Challenge Sections'}</Label>
                        <p className="text-sm text-gray-500 mt-1">
                          {language === 'es' 
                            ? 'Agrega las secciones que necesites. Ej: "Elegibilidad", "Proceso de Postulacion", "Beneficios"'
                            : 'Add the sections you need. E.g.: "Eligibility", "Application Process", "Benefits"'}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="load-sections-template-btn"
                          onClick={() => {
                            const template = language === 'es'
                              ? [
                                  { title: 'Elegibilidad', items: [
                                    { text: 'Startups con domicilio en Chile' },
                                    { text: 'Persona juridica o natural con inicio de actividades' },
                                    { text: 'Solucion vinculada a la tematica del desafio' },
                                  ]},
                                  { title: 'Proceso de Postulacion', items: [
                                    { text: 'Completar formulario en linea' },
                                    { text: 'Adjuntar pitch deck o presentacion del proyecto' },
                                    { text: 'Esperar evaluacion por parte del comite' },
                                  ]},
                                  { title: 'Beneficios', items: [
                                    { text: 'Mentorias con expertos del sector' },
                                    { text: 'Acceso a red de inversionistas' },
                                    { text: 'Premio en capital de trabajo' },
                                  ]},
                                ]
                              : [
                                  { title: 'Eligibility', items: [
                                    { text: 'Startups based in Chile' },
                                    { text: 'Legal or natural person with business registration' },
                                    { text: 'Solution related to the challenge topic' },
                                  ]},
                                  { title: 'Application Process', items: [
                                    { text: 'Complete online application form' },
                                    { text: 'Attach pitch deck or project presentation' },
                                    { text: 'Await evaluation by the committee' },
                                  ]},
                                  { title: 'Benefits', items: [
                                    { text: 'Mentorship with industry experts' },
                                    { text: 'Access to investor network' },
                                    { text: 'Working capital prize' },
                                  ]},
                                ];
                            setProgramForm({ ...programForm, customSections: template });
                          }}
                          className="border-amber-400 text-amber-800 hover:bg-amber-50 font-semibold"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {language === 'es' ? 'Cargar Plantilla' : 'Load Template'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setProgramForm({
                            ...programForm,
                            customSections: [...programForm.customSections, { title: '', items: [] }]
                          })}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t.programs.addSection}
                        </Button>
                      </div>
                    </div>
                    
                    {programForm.customSections.map((section, sectionIndex) => (
                      <div key={sectionIndex} className="border-2 border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex gap-2 items-center">
                          <Input
                            placeholder={language === 'es' ? 'Titulo de la seccion (ej: Elegibilidad)' : 'Section title (e.g.: Eligibility)'}
                            value={section.title}
                            onChange={(e) => {
                              const newSections = [...programForm.customSections];
                              newSections[sectionIndex].title = e.target.value;
                              setProgramForm({ ...programForm, customSections: newSections });
                            }}
                            className="flex-1 font-semibold"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              const newSections = programForm.customSections.filter((_, i) => i !== sectionIndex);
                              setProgramForm({ ...programForm, customSections: newSections });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Items within the section */}
                        <div className="space-y-2 pl-4">
                          {section.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex gap-2 items-center">
                              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-400"></span>
                              <Input
                                placeholder={t.programs.itemPlaceholder}
                                value={item.text}
                                onChange={(e) => {
                                  const newSections = [...programForm.customSections];
                                  newSections[sectionIndex].items[itemIndex].text = e.target.value;
                                  setProgramForm({ ...programForm, customSections: newSections });
                                }}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => {
                                  const newSections = [...programForm.customSections];
                                  newSections[sectionIndex].items = section.items.filter((_, i) => i !== itemIndex);
                                  setProgramForm({ ...programForm, customSections: newSections });
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-gray-800"
                            onClick={() => {
                              const newSections = [...programForm.customSections];
                              newSections[sectionIndex].items = [...section.items, { text: '' }];
                              setProgramForm({ ...programForm, customSections: newSections });
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            {t.programs.addItem}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Form Builder Section */}
              <div className="border-2 border-blue-300 rounded-lg p-5 space-y-4 bg-blue-50">
                <div className="flex items-start gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-lg font-bold text-blue-900">
                      {language === 'es' ? 'Formulario de Postulacion' : 'Application Form'}
                    </Label>
                    <p className="text-sm text-blue-700 mt-1">
                      {language === 'es' 
                        ? 'Define los campos que los postulantes deben completar al aplicar a este desafio. Puedes cargar una plantilla predefinida o generar campos desde los criterios de evaluacion.'
                        : 'Define the fields applicants must fill when applying to this challenge. You can load a predefined template or generate fields from evaluation criteria.'}
                    </p>
                  </div>
                </div>
                <FormBuilder
                  fields={programForm.customFormFields}
                  onChange={(fields) => setProgramForm({ ...programForm, customFormFields: fields })}
                  language={language}
                  scoringCriteria={programForm.scoringCriteria}
                />
              </div>

              {/* Auto-Messages Section */}
              <div className="border-2 border-green-300 rounded-lg p-5 space-y-4 bg-green-50">
                <div className="flex items-start gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg shrink-0">
                    <MessageSquare className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <Label className="text-lg font-bold text-green-900">
                      {language === 'es' ? 'Mensajes Automáticos' : 'Automatic Messages'}
                    </Label>
                    <p className="text-sm text-green-700 mt-1">
                      {language === 'es' 
                        ? 'Configura mensajes que se envían automáticamente a los postulantes cuando ocurren ciertos eventos. Usa {{nombre}}, {{desafio}} y {{organizacion}} como variables.'
                        : 'Configure messages that are automatically sent to applicants when certain events occur. Use {{nombre}}, {{desafio}} and {{organizacion}} as variables.'}
                    </p>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-green-400 text-green-700 hover:bg-green-100"
                  data-testid="add-auto-message-btn"
                  onClick={() => setProgramForm({
                    ...programForm,
                    autoMessages: [...(programForm.autoMessages || []), { event: 'on_submit', subject: '', body: '', enabled: true }]
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {language === 'es' ? 'Agregar Mensaje Automático' : 'Add Auto Message'}
                </Button>

                {(programForm.autoMessages || []).length === 0 && (
                  <p className="text-sm text-green-600 italic">
                    {language === 'es'
                      ? 'Si no configuras mensajes, se enviará un mensaje de confirmación predeterminado al postular.'
                      : 'If you don\'t configure messages, a default confirmation message will be sent on application.'}
                  </p>
                )}

                {(programForm.autoMessages || []).map((am, index) => {
                  const eventLabels = {
                    on_submit: language === 'es' ? 'Al Postular' : 'On Submit',
                    on_review: language === 'es' ? 'En Revisión' : 'On Review',
                    on_finalist: language === 'es' ? 'Finalista' : 'On Finalist',
                    on_accept: language === 'es' ? 'Aceptado' : 'On Accept',
                    on_reject: language === 'es' ? 'Rechazado' : 'On Reject'
                  };
                  return (
                    <div key={index} className="border border-green-300 bg-white rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Select
                            value={am.event}
                            onValueChange={(val) => {
                              const msgs = [...programForm.autoMessages];
                              msgs[index] = { ...msgs[index], event: val };
                              setProgramForm({ ...programForm, autoMessages: msgs });
                            }}
                          >
                            <SelectTrigger className="w-48" data-testid={`auto-msg-event-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="on_submit">{eventLabels.on_submit}</SelectItem>
                              <SelectItem value="on_review">{eventLabels.on_review}</SelectItem>
                              <SelectItem value="on_finalist">{eventLabels.on_finalist}</SelectItem>
                              <SelectItem value="on_accept">{eventLabels.on_accept}</SelectItem>
                              <SelectItem value="on_reject">{eventLabels.on_reject}</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={am.enabled}
                              onChange={(e) => {
                                const msgs = [...programForm.autoMessages];
                                msgs[index] = { ...msgs[index], enabled: e.target.checked };
                                setProgramForm({ ...programForm, autoMessages: msgs });
                              }}
                              className="rounded"
                            />
                            {language === 'es' ? 'Activo' : 'Enabled'}
                          </label>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const msgs = programForm.autoMessages.filter((_, i) => i !== index);
                            setProgramForm({ ...programForm, autoMessages: msgs });
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{language === 'es' ? 'Asunto' : 'Subject'}</Label>
                        <Input
                          data-testid={`auto-msg-subject-${index}`}
                          value={am.subject}
                          onChange={(e) => {
                            const msgs = [...programForm.autoMessages];
                            msgs[index] = { ...msgs[index], subject: e.target.value };
                            setProgramForm({ ...programForm, autoMessages: msgs });
                          }}
                          placeholder={language === 'es' ? 'Ej: Bienvenido a {{desafio}}' : 'E.g: Welcome to {{desafio}}'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-600">{language === 'es' ? 'Mensaje' : 'Message'}</Label>
                        <Textarea
                          data-testid={`auto-msg-body-${index}`}
                          value={am.body}
                          onChange={(e) => {
                            const msgs = [...programForm.autoMessages];
                            msgs[index] = { ...msgs[index], body: e.target.value };
                            setProgramForm({ ...programForm, autoMessages: msgs });
                          }}
                          rows={3}
                          placeholder={language === 'es' ? 'Hola {{nombre}}, gracias por postular a {{desafio}}...' : 'Hello {{nombre}}, thanks for applying to {{desafio}}...'}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setProgramDialogOpen(false)}>
                {t.common.cancel}
              </Button>
              <Button 
                onClick={handleSaveProgram} 
                disabled={saving || !programForm.name}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t.common.save}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* HMW AI Assistant */}
        <HMWAssistant
          open={showHMWAssistant}
          onClose={() => setShowHMWAssistant(false)}
          onAccept={(hmw) => setProgramForm({ ...programForm, challenge: `<p>${hmw}</p>` })}
          language={language}
        />

        {/* Application Detail Dialog */}
        <Dialog open={applicationDetailOpen} onOpenChange={setApplicationDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t.applications.applicationDetails}</DialogTitle>
              <DialogDescription>{selectedApplication?.startupName}</DialogDescription>
            </DialogHeader>
            
            {selectedApplication && (
              <div className="space-y-6 py-4">
                {/* Founder Info */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t.applications.founderInfo}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.applicant}</p>
                      <p className="font-medium">{selectedApplication.founderName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.email}</p>
                      <p className="font-medium">{selectedApplication.email}</p>
                    </div>
                  </div>
                </div>
                
                {/* Startup Info */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t.applications.startupInfo}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.startup}</p>
                      <p className="font-medium">{selectedApplication.startupName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.stage}</p>
                      <Badge variant="outline">{selectedApplication.stage}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.teamSize}</p>
                      <p className="font-medium">{selectedApplication.teamSize || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t.applications.fundingRaised}</p>
                      <p className="font-medium">
                        ${selectedApplication.fundingRaised?.toLocaleString() || 0}
                      </p>
                    </div>
                    {selectedApplication.website && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">{t.applications.website}</p>
                        <a 
                          href={selectedApplication.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:underline"
                        >
                          {selectedApplication.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Description */}
                {selectedApplication.description && (
                  <div>
                    <h4 className="font-semibold mb-2">{t.programs.description}</h4>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.description}
                    </p>
                  </div>
                )}
                
                {/* Pitch Deck */}
                {selectedApplication.pitchDeck && (
                  <div>
                    <h4 className="font-semibold mb-2">{t.applications.pitchDeck}</h4>
                    <a 
                      href={`${API_URL}${selectedApplication.pitchDeck}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-600 hover:underline"
                    >
                      <FileText className="w-4 h-4" />
                      {t.common.download}
                    </a>
                  </div>
                )}
                
                {/* Notes */}
                {selectedApplication.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">{t.applications.notes}</h4>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.notes}
                    </p>
                  </div>
                )}
                
                {/* Evaluations */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    {t.applications.evaluations}
                  </h4>
                  {selectedApplication.scores && selectedApplication.scores.length > 0 ? (
                    <div className="space-y-3">
                      {selectedApplication.scores.map((score, index) => (
                        <div key={index} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{score.evaluatorName || 'Evaluator'}</span>
                            <span className="text-xl font-bold text-yellow-600">
                              {score.weightedScore?.toFixed(1) || '-'} / 10
                            </span>
                          </div>
                          {score.overallComment && (
                            <p className="text-sm text-gray-600">{score.overallComment}</p>
                          )}
                        </div>
                      ))}
                      <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-purple-800">{t.applications.avgScore}</span>
                          <span className="text-2xl font-bold text-purple-600">
                            {(selectedApplication.scores.reduce((acc, s) => acc + (s.weightedScore || 0), 0) / selectedApplication.scores.length).toFixed(1)} / 10
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">{t.applications.noEvaluations}</p>
                  )}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplicationDetailOpen(false)}>
                {t.common.close}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Evaluators Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                {t.evaluators.assignEvaluators}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Evaluator Selection */}
              <div className="space-y-2">
                <Label>{t.evaluators.selectEvaluator} *</Label>
                <Select 
                  value={selectedEvaluator} 
                  onValueChange={(value) => {
                    setSelectedEvaluator(value);
                    setSelectedApplications([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.evaluators.selectEvaluator} />
                  </SelectTrigger>
                  <SelectContent>
                    {evaluators.map((evaluator) => (
                      <SelectItem key={evaluator._id} value={evaluator._id}>
                        {evaluator.name} ({evaluator.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Applications List */}
              {selectedEvaluator && (
                <div className="space-y-2">
                  <Label>
                    {t.evaluators.selectApplications} ({selectedApplications.length} {t.evaluators.selected})
                  </Label>
                  <div className="border-2 border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                    {applications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        {t.applications.noApplications}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {applications.map((app) => {
                          const alreadyAssigned = isAlreadyAssigned(app._id);
                          const isSelected = selectedApplications.includes(app._id);
                          
                          return (
                            <div
                              key={app._id}
                              className={`p-4 flex items-center gap-4 transition-colors ${
                                alreadyAssigned 
                                  ? 'bg-gray-50 opacity-60' 
                                  : isSelected 
                                    ? 'bg-purple-50' 
                                    : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                              onClick={() => !alreadyAssigned && toggleApplicationSelection(app._id)}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={alreadyAssigned}
                                onCheckedChange={() => toggleApplicationSelection(app._id)}
                                className="data-[state=checked]:bg-purple-600"
                              />
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 truncate">
                                    {app.startupName}
                                  </h4>
                                  {alreadyAssigned && (
                                    <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                                      {t.evaluators.alreadyAssigned}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">
                                  {app.founderName} • {app.programName}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                {t.common.cancel}
              </Button>
              <Button 
                onClick={handleAssignEvaluators} 
                disabled={saving || !selectedEvaluator || selectedApplications.length === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t.evaluators.assign} ({selectedApplications.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OrganizationDashboard;
